import { convexQuery } from '@convex-dev/react-query'
import { api } from '@cvx/_generated/api'
import { Turnstile } from '@marsidev/react-turnstile'
import { Button } from '@rekrytering/ui/components/button'
import { Input } from '@rekrytering/ui/components/input'
import { useForm } from '@tanstack/react-form'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Loader2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { env } from '@/env.client'
import { authClient } from '@/lib/auth-client'
import { Route as DashboardRoute } from '@/routes/_app/_auth/dashboard/_layout.index'
import { Route as OnboardingUsernameRoute } from '@/routes/_app/_auth/onboarding/_layout.username'

export function LoginPage() {
  const [step, setStep] = useState<'signIn' | { email: string }>('signIn')
  const { data: session, isPending } = authClient.useSession()
  const { data: user } = useQuery(
    convexQuery(api.features.user.user.getMyCurrentUser, {}),
  )
  const navigate = useNavigate()

  useEffect(() => {
    if (isPending) return
    if (!session) return
    if (!user) return
    if (!user.username) {
      navigate({ to: OnboardingUsernameRoute.fullPath })
      return
    }
    navigate({ to: DashboardRoute.fullPath })
  }, [session, user, isPending])

  if (step === 'signIn') {
    return <LoginForm onSubmit={(email) => setStep({ email })} />
  }
  return <VerifyForm email={step.email} />
}

function LoginForm({ onSubmit }: { onSubmit: (email: string) => void }) {
  const { t } = useTranslation()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [ssoEmail, setSsoEmail] = useState('')
  const [ssoError, setSsoError] = useState<string | undefined>(undefined)
  const [ssoRedirecting, setSsoRedirecting] = useState(false)
  const turnstileTokenRef = useRef<string | undefined>(undefined)

  const ssoEmailDomain = ssoEmail.includes('@')
    ? (ssoEmail.split('@')[1] ?? '')
    : ''

  const { data: ssoLookup } = useQuery(
    convexQuery(api.features.org.org_settings.lookupSsoByEmailDomain, {
      emailDomain: ssoEmailDomain,
    }),
  )

  const form = useForm({
    defaultValues: {
      email: '',
    },
    onSubmit: async ({ value }) => {
      setIsSubmitting(true)
      await authClient.emailOtp.sendVerificationOtp(
        { email: value.email, type: 'sign-in' },
        {
          headers: {
            'x-captcha-response': turnstileTokenRef.current ?? '',
          },
        },
      )
      onSubmit(value.email)
      setIsSubmitting(false)
    },
  })

  const handleSsoSignIn = (): void => {
    setSsoError(undefined)
    if (!ssoLookup?.hasSso) {
      setSsoError(t('auth.login.ssoNotConfigured'))
      return
    }
    setSsoRedirecting(true)
    const callbackUrl = `${window.location.origin}/dashboard`
    window.location.href = `${env.VITE_CONVEX_SITE_URL}/auth/sso/start?orgId=${encodeURIComponent(ssoLookup.organizationId)}&callbackUrl=${encodeURIComponent(callbackUrl)}`
  }

  return (
    <div className="mx-auto flex h-full w-full max-w-96 flex-col items-center justify-center gap-6">
      <div className="mb-2 flex flex-col gap-2">
        <h3 className="text-center text-2xl font-medium text-primary">
          {t('auth.login.title')}
        </h3>
        <p className="text-center text-base font-normal text-primary/60">
          {t('auth.login.subtitle')}
        </p>
      </div>
      <form
        className="flex w-full flex-col items-start gap-1"
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          form.handleSubmit()
        }}
      >
        <form.Field
          name="email"
          validators={{
            onSubmit: z.email(t('auth.login.emailInvalid')).max(256),
          }}
          children={(field) => (
            <div className="flex w-full flex-col gap-1.5">
              <label htmlFor="email" className="sr-only">
                {t('auth.login.emailLabel')}
              </label>
              <Input
                data-testid="email-input"
                placeholder={t('auth.login.emailPlaceholder')}
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => {
                  field.handleChange(e.target.value)
                  setSsoEmail(e.target.value)
                }}
                className={`bg-transparent ${
                  field.state.meta?.errors.length > 0 &&
                  'border-destructive focus-visible:ring-destructive'
                }`}
              />
              {field.state.meta.errors.length > 0 && (
                <span
                  data-testid="email-error"
                  className="text-sm text-destructive dark:text-destructive-foreground"
                >
                  {field.state.meta.errors
                    .map((e) =>
                      typeof e === 'string'
                        ? e
                        : (e as { message: string }).message,
                    )
                    .join(' ')}
                </span>
              )}
            </div>
          )}
        />

        <Turnstile
          siteKey={env.VITE_TURNSTILE_SITE_KEY}
          onSuccess={(token) => {
            turnstileTokenRef.current = token
          }}
          onExpire={() => {
            turnstileTokenRef.current = undefined
          }}
          options={{ size: 'flexible' }}
          className="w-full"
        />

        <Button
          data-testid="submit-email-button"
          type="submit"
          className="w-full"
        >
          {isSubmitting ? (
            <Loader2 className="animate-spin" />
          ) : (
            t('auth.login.continueWithEmail')
          )}
        </Button>
      </form>

      {ssoEmailDomain && ssoLookup?.hasSso && (
        <div className="flex w-full flex-col items-center gap-2">
          <p className="text-sm text-primary/40">{t('auth.login.ssoOr')}</p>
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={handleSsoSignIn}
            disabled={ssoRedirecting}
          >
            {ssoRedirecting ? (
              <>
                <Loader2 className="mr-2 animate-spin" />
                {t('auth.login.ssoRedirecting')}
              </>
            ) : (
              t('auth.login.ssoSignIn')
            )}
          </Button>
          {ssoError && (
            <p className="text-sm text-destructive dark:text-destructive-foreground">
              {ssoError}
            </p>
          )}
        </div>
      )}

      <p className="px-12 text-center text-sm font-normal leading-normal text-primary/60">
        {t('auth.login.termsText')}{' '}
        <a className="underline hover:text-primary">
          {t('auth.login.termsOfService')}
        </a>{' '}
        {t('auth.login.and')}{' '}
        <a className="underline hover:text-primary">
          {t('auth.login.privacyPolicy')}
        </a>
      </p>
    </div>
  )
}

function VerifyForm({ email }: { email: string }) {
  const { t } = useTranslation()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [serverError, setServerError] = useState<string | undefined>(undefined)
  const [codeSent, setCodeSent] = useState(false)

  const form = useForm({
    defaultValues: {
      code: '',
    },
    onSubmit: async ({ value }) => {
      setIsSubmitting(true)
      setServerError(undefined)
      const { error } = await authClient.emailOtp.verifyEmail({
        email,
        otp: value.code,
      })
      if (error) {
        setServerError(t('auth.verify.invalidCode'))
      }
      setIsSubmitting(false)
    },
  })

  const handleRequestNewCode = async (): Promise<void> => {
    setCodeSent(false)
    await authClient.emailOtp.sendVerificationOtp({ email, type: 'sign-in' })
    setCodeSent(true)
  }

  return (
    <div className="mx-auto flex h-full w-full max-w-96 flex-col items-center justify-center gap-6">
      <div className="mb-2 flex flex-col gap-2">
        <p
          data-testid="verify-title"
          className="text-center text-2xl text-primary"
        >
          {t('auth.verify.title')}
        </p>
        <p className="text-center text-base font-normal text-primary/60">
          {t('auth.verify.subtitle')}
        </p>
      </div>
      <form
        className="flex w-full flex-col items-start gap-1"
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          form.handleSubmit()
        }}
      >
        <form.Field
          name="code"
          validators={{
            onSubmit: z.string().min(6, t('auth.verify.codeMinLength')),
          }}
          children={(field) => (
            <div className="flex w-full flex-col gap-1.5">
              <label htmlFor="code" className="sr-only">
                {t('auth.verify.codeLabel')}
              </label>
              <Input
                data-testid="code-input"
                placeholder={t('auth.verify.codePlaceholder')}
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                className={`bg-transparent ${
                  field.state.meta?.errors.length > 0 &&
                  'border-destructive focus-visible:ring-destructive'
                }`}
              />
              {field.state.meta.errors.length > 0 && (
                <span
                  data-testid="code-error"
                  className="text-sm text-destructive dark:text-destructive-foreground"
                >
                  {field.state.meta.errors
                    .map((e) =>
                      typeof e === 'string'
                        ? e
                        : (e as { message: string }).message,
                    )
                    .join(' ')}
                </span>
              )}
            </div>
          )}
        />

        {serverError && (
          <span
            data-testid="verify-error"
            className="text-sm text-destructive dark:text-destructive-foreground"
          >
            {serverError}
          </span>
        )}

        <Button
          data-testid="submit-code-button"
          type="submit"
          className="w-full"
        >
          {isSubmitting ? (
            <Loader2 className="animate-spin" />
          ) : (
            t('auth.verify.continue')
          )}
        </Button>
      </form>

      <div className="flex w-full flex-col">
        <p className="text-center text-sm font-normal text-primary/60">
          {t('auth.verify.didNotReceive')}
        </p>
        <Button
          data-testid="request-new-code-button"
          onClick={handleRequestNewCode}
          variant="ghost"
          className="w-full hover:bg-transparent"
        >
          {t('auth.verify.requestNewCode')}
        </Button>
        {codeSent && (
          <p
            data-testid="code-sent-confirmation"
            className="text-center text-sm text-primary/60"
          >
            {t('auth.verify.codeSent')}
          </p>
        )}
      </div>
    </div>
  )
}
